import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Category,
  ChevronLeft,
  ChevronRight,
  GridView,
  Layers,
} from '@mui/icons-material';
import moment from 'moment';
import { alpha } from '@mui/material/styles';
import {
  dateToMoment,
  momentToDate,
  CALENDAR_GRID_DAY_COUNT,
  CALENDAR_GRID_DAY_OPTIONS,
} from '../../utils/dateUtils';
import { DASHBOARD_ACCENT, resolveDashboardThemeMode } from '../../utils/dashboardSurfaceSx';
import {
  getShellMenuItemSx,
  getShellMenuPaperSx,
} from '../../utils/mobileShellMenuLayout';
import './BookingCalendar.css';
import { getHotelDateFormat } from '../../utils/dateUtils';

const CUSTOM_BORDER_COLOR = DASHBOARD_ACCENT;

/** Shared height for dashboard workspace filter controls (matches nav button CSS). */
const WORKSPACE_CONTROL_HEIGHT = 36;

const GROUP_VIEW_LABELS = {
  full: 'Full View',
  floor: 'Floor View',
  room_type: 'Room Type View',
};

const GROUP_VIEW_ICONS = {
  full: GridView,
  floor: Layers,
  room_type: Category,
};

const groupViewMenuFocusProps = {
  autoFocus: false,
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
  MenuListProps: { autoFocusItem: false, sx: { py: 0 } },
};

const groupViewMenuPaperSx = (isDark) => ({
  ...getShellMenuPaperSx(isDark),
  width: 240,
  minWidth: 240,
  borderRadius: '12px',
  padding: '8px 0',
  marginTop: '8px',
});

const groupViewMenuItemSx = (isDark, selected) => ({
  ...getShellMenuItemSx(isDark),
  height: 40,
  px: 2,
  ...(selected
    ? {
        backgroundColor: isDark ? alpha('#4ce1b6', 0.1) : 'rgba(76, 225, 182, 0.08)',
        '&:hover': {
          backgroundColor: isDark ? alpha('#4ce1b6', 0.14) : 'rgba(76, 225, 182, 0.12)',
        },
      }
    : null),
});

/**
 * Booking Calendar Controls Component
 * Handles Material UI date picker, view toggles, and filter dropdown
 */
const BookingCalendarControls = ({
  displayView,
  onViewChange,
  currentDate,
  onDateNavigation,
  onDateChange,
  groupView = 'full', // 'full', 'floor', 'room_type'
  onGroupViewChange,
  gridDayCount = CALENDAR_GRID_DAY_COUNT,
  onGridDayCountChange,
  additionalActions = null, // Optional content to render after controls
  variant = 'default',
  themeMode = 'dark',
}) => {
  const theme = useTheme();
  const isWorkspace = variant === 'workspace';
  const isDark = isWorkspace ? resolveDashboardThemeMode(themeMode) === 'dark' : theme.palette.mode === 'dark';
  const [groupViewAnchor, setGroupViewAnchor] = useState(null);

  const mainDisplayMode =
    displayView === 'calendar' || displayView === 'ultra' || displayView === 'month'
      ? displayView
      : 'ultra';
  
  // Simplified date handling - work with date strings and local dates
  // This avoids timezone conversion issues while keeping the datepicker and calendar in sync
  
  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      const dateObj = momentToDate(newDate);
      if (dateObj) {
        onDateChange(dateObj);
      }
    }
  };

  const handlePrevClick = () => {
    const currentMoment = moment(currentDate);
    if (displayView === 'month') {
      const newMoment = currentMoment.clone().subtract(1, 'month');
      const dateObj = momentToDate(newMoment);
      if (dateObj) onDateChange(dateObj);
      return;
    }
    const days = displayView === 'ultra' ? 1 : gridDayCount;
    const newMoment = currentMoment.clone().subtract(days, 'days');
    const dateObj = momentToDate(newMoment);
    if (dateObj) {
      onDateChange(dateObj);
    }
  };

  const handleNextClick = () => {
    const currentMoment = moment(currentDate);
    if (displayView === 'month') {
      const newMoment = currentMoment.clone().add(1, 'month');
      const dateObj = momentToDate(newMoment);
      if (dateObj) onDateChange(dateObj);
      return;
    }
    const days = displayView === 'ultra' ? 1 : gridDayCount;
    const newMoment = currentMoment.clone().add(days, 'days');
    const dateObj = momentToDate(newMoment);
    if (dateObj) {
      onDateChange(dateObj);
    }
  };
  
  // Convert currentDate to moment for DatePicker using utility
  const currentDateMoment = useMemo(() => {
    return dateToMoment(currentDate);
  }, [currentDate]);

  const workspaceControlSx = isWorkspace
    ? {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: { xs: 0.75, sm: 1 },
        flexWrap: 'wrap',
        width: '100%',
        minWidth: 0,
        minHeight: WORKSPACE_CONTROL_HEIGHT,
      }
    : {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        mb: 0,
        minHeight: { sm: 44 },
      };

  const navButtonSx = isWorkspace
    ? {
        width: WORKSPACE_CONTROL_HEIGHT,
        height: WORKSPACE_CONTROL_HEIGHT,
        minWidth: WORKSPACE_CONTROL_HEIGHT,
        minHeight: WORKSPACE_CONTROL_HEIGHT,
        borderRadius: '10px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        color: isDark ? '#e2e8f0' : '#334155',
        flexShrink: 0,
      }
    : {
        borderRadius: '4px',
        width: '36px',
        height: '36px',
        padding: 0,
      };

  const dateFieldSx = isWorkspace
    ? {
        '& .MuiOutlinedInput-root': {
          height: WORKSPACE_CONTROL_HEIGHT,
          minHeight: WORKSPACE_CONTROL_HEIGHT,
          borderRadius: '10px',
          fontSize: '0.8125rem',
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          color: isDark ? '#f8fafc' : '#0f172a',
          '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
          '&:hover fieldset': { borderColor: 'rgba(76, 225, 182, 0.35)' },
          '&.Mui-focused fieldset': { borderColor: CUSTOM_BORDER_COLOR },
        },
        '& .MuiInputBase-input': {
          py: 0,
          px: 1,
          height: WORKSPACE_CONTROL_HEIGHT,
          boxSizing: 'border-box',
          textAlign: 'center',
        },
        '& .MuiInputAdornment-root .MuiIconButton-root': {
          width: 28,
          height: 28,
        },
      }
    : {
        '& .MuiOutlinedInput-root': {
          height: '36px',
          fontSize: '14px',
        },
        '& .MuiInputBase-input': {
          padding: '8px 12px',
          textAlign: 'center',
        },
      };

  const groupButtonSx = isWorkspace
    ? {
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        color: isDark ? '#f8fafc' : '#0f172a',
        fontWeight: 700,
        fontSize: '0.8125rem',
        minWidth: { xs: 112, sm: 132 },
        height: WORKSPACE_CONTROL_HEIGHT,
        minHeight: WORKSPACE_CONTROL_HEIGHT,
        ml: 0,
        px: 1.25,
        textTransform: 'none',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: '10px',
        flexShrink: 0,
        '& .MuiButton-endIcon': {
          ml: 0.35,
          '& > *:nth-of-type(1)': { fontSize: 20 },
        },
        '&:hover': {
          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
          borderColor: 'rgba(76, 225, 182, 0.35)',
        },
      }
    : {
        backgroundColor: CUSTOM_BORDER_COLOR,
        color: '#000',
        fontWeight: 600,
        fontSize: '0.875rem',
        minWidth: 160,
        height: '36px',
        ml: 1,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: CUSTOM_BORDER_COLOR,
          opacity: 0.9,
        },
      };

  const workspaceToggleSelectedSx = {
    backgroundColor: `${alpha('#ffffff', isDark ? 0.08 : 0.1)} !important`,
    color: `${isDark ? '#e2e8f0' : '#0f172a'} !important`,
    boxShadow: `inset 0 0 0 1px ${alpha(CUSTOM_BORDER_COLOR, isDark ? 0.2 : 0.28)}`,
  };

  const toggleGroupSx = isWorkspace
    ? {
        ml: 0,
        height: WORKSPACE_CONTROL_HEIGHT,
        minHeight: WORKSPACE_CONTROL_HEIGHT,
        boxSizing: 'border-box',
        alignItems: 'stretch',
        py: 0,
        px: 0.35,
        flexWrap: 'nowrap',
        flexShrink: 0,
        borderRadius: '12px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        background: isDark ? '#161922' : 'rgba(0,0,0,0.02)',
        '& .MuiToggleButtonGroup-grouped': {
          border: 0,
          borderRadius: '10px !important',
          mx: 0.15,
          minHeight: 'unset',
          height: 'auto',
          alignSelf: 'stretch',
          display: 'inline-flex',
          alignItems: 'center',
          px: { xs: 0.75, sm: 1.1 },
          py: 0,
          fontSize: '0.62rem',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? 'rgba(248,250,252,0.45)' : 'rgba(15,23,42,0.45)',
          '&.Mui-selected': workspaceToggleSelectedSx,
          '&.Mui-selected:hover': workspaceToggleSelectedSx,
        },
      }
    : {
        ml: { xs: 0, sm: 1 },
        mt: { xs: 1, sm: 0 },
        py: 0.65,
        px: 0.65,
        flexWrap: 'wrap',
        justifyContent: 'center',
        borderRadius: '26px',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        background:
          theme.palette.mode === 'dark'
            ? 'rgba(42, 42, 49, 0.8)'
            : 'rgba(245, 245, 250, 0.8)',
        '& .MuiToggleButtonGroup-grouped': {
          border: 0,
          borderRadius: '20px !important',
          mx: 0.25,
          minHeight: 36,
          px: { xs: 1.35, sm: 1.65 },
          py: 1,
          fontSize: { xs: '11px', sm: '12px' },
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.42)'
              : 'rgba(0, 0, 0, 0.45)',
          '&.Mui-selected': {
            backgroundColor: `${CUSTOM_BORDER_COLOR} !important`,
            color: '#000 !important',
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${CUSTOM_BORDER_COLOR} !important`,
            opacity: 0.93,
          },
        },
      };

  const dayRangeToggleSx = isWorkspace
    ? {
        height: WORKSPACE_CONTROL_HEIGHT,
        minHeight: WORKSPACE_CONTROL_HEIGHT,
        boxSizing: 'border-box',
        alignItems: 'stretch',
        py: 0,
        px: 0.35,
        borderRadius: '12px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        '& .MuiToggleButtonGroup-grouped': {
          border: 0,
          borderRadius: '10px !important',
          mx: 0.15,
          minHeight: 'unset',
          height: 'auto',
          alignSelf: 'stretch',
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: 42,
          px: 0.85,
          py: 0,
          fontSize: '0.62rem',
          fontWeight: 800,
          textTransform: 'none',
          letterSpacing: '0.02em',
          color: isDark ? 'rgba(248,250,252,0.45)' : 'rgba(15,23,42,0.45)',
          '&.Mui-selected': workspaceToggleSelectedSx,
          '&.Mui-selected:hover': workspaceToggleSelectedSx,
        },
      }
    : {
        ml: 1,
        '& .MuiToggleButtonGroup-grouped': {
          minHeight: 32,
          px: 1.25,
          fontSize: '0.75rem',
          fontWeight: 700,
          '&.Mui-selected': {
            backgroundColor: `${CUSTOM_BORDER_COLOR} !important`,
            color: '#000 !important',
          },
        },
      };

  const boardTypeToggle = (
    <ToggleButtonGroup
      exclusive
      value={mainDisplayMode}
      onChange={(e, value) => {
        if (value != null) onViewChange(value);
      }}
      aria-label="Dashboard booking layout"
      size="small"
      sx={toggleGroupSx}
    >
      <ToggleButton value="ultra">Grid</ToggleButton>
      <ToggleButton value="calendar">Ultra</ToggleButton>
      <ToggleButton value="month">Calendar</ToggleButton>
    </ToggleButtonGroup>
  );

  const groupViewControl = onGroupViewChange ? (
    <>
      <Button
        variant={isWorkspace ? 'outlined' : 'contained'}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setGroupViewAnchor(e.currentTarget)}
        sx={groupButtonSx}
      >
        {GROUP_VIEW_LABELS[groupView] ?? groupView}
      </Button>
      <Menu
        anchorEl={groupViewAnchor}
        open={Boolean(groupViewAnchor)}
        onClose={() => setGroupViewAnchor(null)}
        {...groupViewMenuFocusProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isWorkspace ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isWorkspace ? 'right' : 'left',
        }}
        PaperProps={{ sx: groupViewMenuPaperSx(isDark) }}
      >
        {(['full', 'floor', 'room_type']).map((key) => {
          const Icon = GROUP_VIEW_ICONS[key];
          const selected = groupView === key;
          return (
            <MenuItem
              key={key}
              selected={selected}
              onClick={() => {
                onGroupViewChange(key);
                setGroupViewAnchor(null);
              }}
              sx={groupViewMenuItemSx(isDark, selected)}
            >
              <ListItemIcon>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={GROUP_VIEW_LABELS[key]}
                primaryTypographyProps={{ fontSize: '14px', fontWeight: 600 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  ) : null;

  const dateNavigationControl = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
      }}
      className="dashboard-date-range"
    >
      <IconButton
        onClick={handlePrevClick}
        className="calendar-nav-button"
        sx={navButtonSx}
      >
        <ChevronLeft />
      </IconButton>
      <Box sx={{ minWidth: isWorkspace ? 148 : '200px' }} className="calendar-date-picker">
        <DatePicker
          key={currentDateMoment ? currentDateMoment.format('YYYY-MM-DD') : 'no-date'}
          value={currentDateMoment}
          onChange={handleDateChange}
          format={getHotelDateFormat()}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: dateFieldSx,
            },
          }}
        />
      </Box>
      <IconButton
        onClick={handleNextClick}
        className="calendar-nav-button"
        sx={navButtonSx}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );

  const workspaceDayRangeToggle =
    displayView === 'calendar' && onGridDayCountChange && gridDayCount != null ? (
      <ToggleButtonGroup
        exclusive
        value={gridDayCount}
        onChange={(e, value) => {
          if (value != null) onGridDayCountChange(value);
        }}
        aria-label="Calendar day range"
        size="small"
        sx={{
          ...dayRangeToggleSx,
          height: WORKSPACE_CONTROL_HEIGHT,
          minHeight: WORKSPACE_CONTROL_HEIGHT,
          width: 'auto',
          flexShrink: 0,
          '& .MuiToggleButtonGroup-grouped': {
            ...dayRangeToggleSx['& .MuiToggleButtonGroup-grouped'],
            minWidth: 34,
            px: 0.75,
            fontSize: '0.68rem',
            fontWeight: 800,
          },
        }}
      >
        {CALENDAR_GRID_DAY_OPTIONS.map((days) => (
          <ToggleButton key={days} value={days}>
            {days}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ) : null;

  if (isWorkspace) {
    return (
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            ...workspaceControlSx,
            alignItems: 'center',
            minHeight: WORKSPACE_CONTROL_HEIGHT,
            width: 'auto',
            maxWidth: '100%',
            ml: 'auto',
          }}
          className="dashboard-control-panel dashboard-control-panel--workspace"
        >
          {boardTypeToggle}
          {groupViewControl}
          {dateNavigationControl}
          {workspaceDayRangeToggle}
          {additionalActions}
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={workspaceControlSx} className="dashboard-control-panel">
        {/* Material UI Date Picker */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'center', sm: 'flex-start' },
            minHeight: { sm: 44 },
          }}
          className="dashboard-date-range"
        >
          <IconButton
            onClick={handlePrevClick}
            className="calendar-nav-button"
            sx={navButtonSx}
          >
            <ChevronLeft />
          </IconButton>
          <Box sx={{ minWidth: '200px' }} className="calendar-date-picker">
            <DatePicker
              key={currentDateMoment ? currentDateMoment.format('YYYY-MM-DD') : 'no-date'}
              value={currentDateMoment}
              onChange={handleDateChange}
              format={getHotelDateFormat()}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: dateFieldSx,
                },
              }}
            />
          </Box>
          <IconButton
            onClick={handleNextClick}
            className="calendar-nav-button"
            sx={navButtonSx}
          >
            <ChevronRight />
          </IconButton>
          
          {groupViewControl}

          {displayView === 'calendar' && onGridDayCountChange && (
            <ToggleButtonGroup
              exclusive
              value={gridDayCount}
              onChange={(e, value) => {
                if (value != null) onGridDayCountChange(value);
              }}
              aria-label="Calendar day range"
              size="small"
              sx={dayRangeToggleSx}
            >
              {CALENDAR_GRID_DAY_OPTIONS.map((days) => (
                <ToggleButton key={days} value={days}>
                  {days}D
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          
          {boardTypeToggle}
        </Box>

        {/* Right Controls - Additional actions (e.g., Add New dropdown) */}
        <Box
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            alignItems: 'center',
            minHeight: { sm: 44 },
          }}
          className="dashboard-control-right"
        >
          {additionalActions}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default BookingCalendarControls;

